import { Link, useNavigate } from "react-router-dom";
import "./ajouterpm.css";
import { useEffect, useState } from "react";
import { ArticleInfo } from "../../../Modeles/ArticleModel";
import * as XLSX from "xlsx";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImportResult {
  message?: string;
  error?: string;
  updates: number;
  creates: number;
  errors: ImportError[];
}

interface ImportError {
  type?: string;
  ref?: string;
  row?: any;
  message?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true when the result should be treated as a full failure.
 * Even if the HTTP status is 200, if nothing was created or updated
 * there is no point showing a success popup.
 */
const isFullFailure = (result: ImportResult): boolean =>
  result.creates === 0 && result.updates === 0;

/** Human-readable label for one error object */
const errorLabel = (err: ImportError | string): string => {
  if (typeof err === "string") return err;
  if (err.type === "missing_ref")         return "Référence manquante dans une ligne";
  if (err.type === "missing_designation") return `Désignation manquante — Réf : ${err.ref}`;
  if (err.type === "missing_column")      return err.message ?? "Colonne manquante dans l'onglet";
  return err.message ?? JSON.stringify(err);
};

// ─── SuccessPopup ─────────────────────────────────────────────────────────────

const SuccessPopup = ({
  result,
  onClose,
}: {
  result: ImportResult;
  onClose: () => void;
}) => (
  <div className="ug-popup-overlay" onClick={onClose}>
    <div className="ug-popup-box ug-popup-success" onClick={(e) => e.stopPropagation()}>
      <button className="ug-popup-close" onClick={onClose}>
        <i className="bi bi-x-lg" />
      </button>

      <div className="ug-popup-icon-wrap success">
        <i className="bi bi-check-circle-fill" />
      </div>

      <h5 className="ug-popup-title">Importation réussie !</h5>
      <p className="ug-popup-subtitle">Votre fichier a été traité avec succès.</p>

      <div className="ug-popup-stats">
        <div className="ug-stat-card created">
          <span className="ug-stat-number">{result.creates}</span>
          <span className="ug-stat-label">
            <i className="bi bi-plus-circle me-1" />Créés
          </span>
        </div>
        <div className="ug-stat-card updated">
          <span className="ug-stat-number">{result.updates}</span>
          <span className="ug-stat-label">
            <i className="bi bi-arrow-repeat me-1" />Mis à jour
          </span>
        </div>
      </div>

      {result.errors?.length > 0 && (
        <div className="ug-popup-warnings">
          <p className="ug-warning-title">
            <i className="bi bi-exclamation-triangle-fill me-1" />
            {result.errors.length} avertissement{result.errors.length > 1 ? "s" : ""}
          </p>
          <ul className="ug-warning-list">
            {result.errors.slice(0, 5).map((err, i) => (
              <li key={i}>{errorLabel(err)}</li>
            ))}
            {result.errors.length > 5 && (
              <li className="ug-warning-more">+{result.errors.length - 5} autres…</li>
            )}
          </ul>
        </div>
      )}

      <button className="ug-popup-btn-primary" onClick={onClose}>
        Continuer
      </button>
    </div>
  </div>
);

// ─── ErrorPopup ───────────────────────────────────────────────────────────────

const ErrorPopup = ({
  errors,
  generalMessage,
  onClose,
}: {
  errors?: ImportError[];
  generalMessage?: string;
  onClose: () => void;
}) => (
  <div className="ug-popup-overlay" onClick={onClose}>
    <div className="ug-popup-box ug-popup-error" onClick={(e) => e.stopPropagation()}>
      <button className="ug-popup-close" onClick={onClose}>
        <i className="bi bi-x-lg" />
      </button>

      <div className="ug-popup-icon-wrap error">
        <i className="bi bi-x-circle-fill" />
      </div>

      <h5 className="ug-popup-title">Échec de l'importation</h5>
      <p className="ug-popup-subtitle">
        {generalMessage ?? "Aucun article n'a été créé ou mis à jour."}
      </p>

      {errors && errors.length > 0 && (
        <div className="ug-popup-error-list">
          <p className="ug-error-list-title">
            <i className="bi bi-list-ul me-1" />Détails des erreurs :
          </p>
          <ul className="ug-error-items">
            {errors.map((err, i) => (
              <li key={i}>
                <i className="bi bi-dot" />
                {errorLabel(err)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button className="ug-popup-btn-danger" onClick={onClose}>
        Fermer
      </button>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AjouterProduitM: React.FC<any> = ({
  setDesignation,
  setPrixVenteArticleTTC,
  setDescription,
  setstock,
  setRefARticle,
  handleImage,
  setprix_ht_2_magasin,
  setquantité,
  setprix_ht_3_magasin,
  setprix_ht_1_magasin,
  handleSubmit,
  setUnite,
  messageError,
  loading: propLoading,
  successResult: manualSuccessResult,
  handleSuccessClose: manualHandleSuccessClose,
}) => {
  useEffect(() => {
    // Previously this page was forcing a body-scale layout hack.
    // The app now has a global responsive system + sidebar offset,
    // so no per-page body manipulation is needed.
  }, []);

  const [internalLoading, setInternalLoading] = useState(false);
  const loading = propLoading || internalLoading;
  const [successResult, setSuccessResult] = useState<ImportResult | null>(null);
  const [errorState, setErrorState]       = useState<{
    errors?: ImportError[];
    generalMessage?: string;
  } | null>(null);

  // Use props if provided, otherwise local state
  const currentSuccessResult = manualSuccessResult || successResult;

  const handleSuccessClose = () => {
    if (manualHandleSuccessClose) {
      manualHandleSuccessClose();
    } else {
      setSuccessResult(null);
      navigate(`/magasins/${MagasinId}`);
    }
  };

  const MagasinId   = localStorage.getItem("MagasinId");
  const MagasinName = localStorage.getItem("MagasinName");
  const navigate    = useNavigate();

  // ── Excel → JSON ──────────────────────────────────────────────────────────
  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const data     = new Uint8Array(event.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet    = workbook.Sheets[workbook.SheetNames[0]];
      sendDataToLaravel(XLSX.utils.sheet_to_json(sheet));
    };
    reader.readAsArrayBuffer(file);
    e.target.value = ""; // allow re-upload of same file
  };

  // ── POST to Laravel ───────────────────────────────────────────────────────
  const sendDataToLaravel = async (articles: any[]) => {
    setErrorState(null);
    setSuccessResult(null);
    setInternalLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PHP_APP_URL}/magasin/${MagasinName}/uploadExcel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(articles),
        }
      );

      const result: ImportResult = await response.json();
      console.log("Laravel response:", result);

      // ── KEY FIX ───────────────────────────────────────────────────────────
      // Show the ERROR popup when:
      //   1. HTTP status is not 2xx  (server returned 4xx / 5xx)
      //   2. OR the server returned 200 but creates=0 AND updates=0,
      //      meaning nothing actually happened (column mismatch, all refs
      //      missing, sheet empty, etc.)
      //
      // Show SUCCESS popup ONLY when at least one row was created or updated.
      // ─────────────────────────────────────────────────────────────────────
      if (!response.ok || isFullFailure(result)) {
        setErrorState({
          generalMessage:
            result.error ??
            result.message ??
            "Aucun article n'a été importé. Vérifiez votre fichier.",
          errors: result.errors ?? [],
        });
      } else {
        setSuccessResult(result);
      }
    } catch (err) {
      console.error("Network error:", err);
      setErrorState({
        generalMessage: "Erreur de connexion au serveur. Veuillez réessayer.",
      });
    } finally {
      setInternalLoading(false);
    }
  };


  const downloadExcelModel = () => {
    const data = [{
      IdArticle: "", RefArticle: "", Designation: "", DesignationAr: "",
      "Quantité": "", NomMarque: "", PrixNetHT: "", urlImage: "",
    }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Model");
    XLSX.writeFile(wb, "Wyn_Model_Excel.xlsx");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <NewSideBarMagasins />

      {currentSuccessResult && (
        <SuccessPopup result={currentSuccessResult} onClose={handleSuccessClose} />
      )}
      {errorState && (
        <ErrorPopup
          errors={errorState.errors}
          generalMessage={errorState.generalMessage}
          onClose={() => setErrorState(null)}
        />
      )}

      {loading ? (
        <div className="ug-apm-loader-container d-flex justify-content-center align-items-center">
          <div className="spinner-border ug-apm-custom-spinner" role="status" />
          <span className="ug-apm-loader-text">Chargement…</span>
        </div>
      ) : (
        <div className="ug-apm-container container mt-3">

          <div className="ug-apm-bar-retour">
            <span className="ug-apm-icon-retour">
              <Link to={""} onClick={() => window.history.back()}>
                <i className="bi bi-arrow-left-short" />
              </Link>
            </span>
          </div>

          <div className="ug-apm-header">
            <h4 className="ug-apm-title">Ajouter un nouveau produit magasin</h4>
            <div className="ug-apm-action-buttons">
              <label className="ug-apm-importation-excel" htmlFor="excel-upload">
                <input
                  id="excel-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  className="ug-apm-file-input"
                  onChange={handleExcelUpload}
                />
                <i className="bi bi-upload ug-apm-button-icon" />
                Importer fichier Excel
              </label>
              <label className="ug-apm-importation-excel" onClick={downloadExcelModel}>
                <i className="bi bi-cloud-download ug-apm-button-icon" />
                Télécharger modèle Excel
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ug-apm-form">
            {messageError !== "" && (
              <div className="alert alert-danger ug-apm-alert-danger" role="alert">
                {messageError}
              </div>
            )}

            <div className="row ug-apm-row-magasin" id="rowMagasin">

              {/* Left column */}
              <div className="col ug-apm-inputs">
                <div className="ug-apm-form-group">
                  <label className="ug-apm-label">Nom de produit <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    onChange={(e) => setDesignation(e.target.value)}
                    className="form-control ug-apm-input mb-3"
                    required
                  />
                </div>

                <div className="ug-apm-form-group ug-apm-description-group">
                  <label className="ug-apm-label ug-apm-span-desc">Description (Optionnel)</label>
                  <textarea
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-control ug-apm-textarea"
                    placeholder="Entrez la description du produit…"
                    defaultValue=""
                  />
                </div>

                <div className="ug-apm-form-group">
                  <label className="ug-apm-label">Catégorie (Optionnel)</label>
                  <input type="text" className="form-control ug-apm-input mb-3" />
                </div>

                <div className="row ug-apm-row-inline mt-3">
                  <div className="col">
                    <label className="ug-apm-label">Quantité <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      onChange={(e) => setquantité(e.target.value)}
                      className="form-control ug-apm-input"
                      defaultValue="1"
                      required
                    />
                  </div>
                  <div className="col">
                    <label className="ug-apm-label">Référence <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      onChange={(e) => setRefARticle(e.target.value)}
                      className="form-control ug-apm-input"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="col ug-apm-photo-section">
                <div className="ug-apm-text-upload text-center">
                  <span className="ug-apm-span-photo">Photo de produit (Optionnel)</span>
                </div>
                <div className="ug-apm-photo-container">
                  <input
                    onChange={handleImage}
                    type="file"
                    id="ImportPhotos"
                    className="ug-apm-file-input-hidden"
                  />
                  <label className="ug-apm-photo-upload" htmlFor="ImportPhotos">
                    <span className="ug-apm-photo-icon"><i className="bi bi-images" /></span>
                    <span className="ug-apm-photo-text">Importer une photo de produit</span>
                    <span className="ug-apm-photo-formats">PNG, JPG, JPEG (Max 5MB)</span>
                  </label>
                </div>

                <div className="row ug-apm-row-pricing mt-3">
                  <div className="col">
                    <label className="ug-apm-label">Prix Net HT <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      onChange={(e) => setprix_ht_1_magasin(e.target.value)}
                      className="form-control ug-apm-input-price"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="col ug-apm-col-magasin">
                  <label className="ug-apm-label">Unité</label>
                  <input
                    type="number"
                    onChange={(e) => setUnite(e.target.value)}
                    className="form-control ug-apm-input"
                    defaultValue={1}
                  />
                  <br />
                  {messageError && (
                    <div className="alert alert-danger p-2" style={{ fontSize: '0.8rem' }}>
                      <i className="bi bi-exclamation-circle me-1" />
                      {messageError}
                    </div>
                  )}
                  <button type="submit" className="ug-apm-btn-confirm">
                    Confirmer
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Scoped popup styles */}
      <style>{`
        .ug-popup-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,.55);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999;
          animation: ug-fadeIn .2s ease;
        }
        @keyframes ug-fadeIn { from{opacity:0} to{opacity:1} }

        .ug-popup-box {
          position: relative; background: #fff; border-radius: 20px;
          padding: 40px 36px 32px; width: 100%; max-width: 440px;
          box-shadow: 0 24px 60px rgba(0,0,0,.18);
          animation: ug-slideUp .25s cubic-bezier(.34,1.56,.64,1);
          text-align: center;
        }
        @keyframes ug-slideUp {
          from{opacity:0;transform:translateY(32px) scale(.96)}
          to  {opacity:1;transform:translateY(0)    scale(1)}
        }

        .ug-popup-close {
          position:absolute; top:14px; right:16px;
          background:#f1f3f5; border:none; border-radius:50%;
          width:32px; height:32px;
          display:flex; align-items:center; justify-content:center;
          cursor:pointer; color:#555; font-size:13px; transition:background .15s;
        }
        .ug-popup-close:hover{background:#e0e3e7}

        .ug-popup-icon-wrap{
          width:68px; height:68px; border-radius:50%;
          display:flex; align-items:center; justify-content:center;
          font-size:32px; margin:0 auto 18px;
        }
        .ug-popup-icon-wrap.success{background:#d1fae5;color:#059669}
        .ug-popup-icon-wrap.error  {background:#fee2e2;color:#dc2626}

        .ug-popup-title   {font-size:1.2rem;font-weight:700;color:#111;margin-bottom:6px}
        .ug-popup-subtitle{font-size:.9rem;color:#6b7280;margin-bottom:22px}

        .ug-popup-stats{display:flex;gap:14px;justify-content:center;margin-bottom:22px}
        .ug-stat-card{
          flex:1;border-radius:12px;padding:14px 10px;
          display:flex;flex-direction:column;align-items:center;gap:4px;
        }
        .ug-stat-card.created{background:#eff6ff;border:1.5px solid #bfdbfe}
        .ug-stat-card.updated{background:#fefce8;border:1.5px solid #fde68a}
        .ug-stat-number{font-size:2rem;font-weight:800;line-height:1}
        .ug-stat-card.created .ug-stat-number{color:#2563eb}
        .ug-stat-card.updated .ug-stat-number{color:#d97706}
        .ug-stat-label{
          font-size:.78rem;font-weight:600;color:#6b7280;
          text-transform:uppercase;letter-spacing:.04em;
        }

        .ug-popup-warnings{
          background:#fffbeb;border:1.5px solid #fde68a;
          border-radius:10px;padding:12px 14px;text-align:left;margin-bottom:20px;
        }
        .ug-warning-title{font-size:.83rem;font-weight:700;color:#b45309;margin-bottom:6px}
        .ug-warning-list{margin:0;padding-left:16px;font-size:.8rem;color:#78350f}
        .ug-warning-more{color:#92400e;font-style:italic}

        .ug-popup-error-list{
          background:#fef2f2;border:1.5px solid #fecaca;
          border-radius:10px;padding:12px 14px;text-align:left;
          margin-bottom:20px;max-height:180px;overflow-y:auto;
        }
        .ug-error-list-title{font-size:.83rem;font-weight:700;color:#b91c1c;margin-bottom:6px}
        .ug-error-items{margin:0;padding:0;list-style:none;font-size:.8rem;color:#7f1d1d}
        .ug-error-items li{padding:3px 0;border-bottom:1px solid #fee2e2}
        .ug-error-items li:last-child{border-bottom:none}

        .ug-popup-btn-primary{
          width:100%;padding:11px;border-radius:10px;border:none;
          background:#059669;color:#fff;font-weight:700;font-size:.95rem;
          cursor:pointer;transition:background .15s,transform .1s;
        }
        .ug-popup-btn-primary:hover{background:#047857;transform:translateY(-1px)}

        .ug-popup-btn-danger{
          width:100%;padding:11px;border-radius:10px;border:none;
          background:#dc2626;color:#fff;font-weight:700;font-size:.95rem;
          cursor:pointer;transition:background .15s,transform .1s;
        }
        .ug-popup-btn-danger:hover{background:#b91c1c;transform:translateY(-1px)}
      `}</style>
    </>
  );
};

export default AjouterProduitM;